import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  useForm,
  SupportedCurrencies,
  FieldData,
  TextField,
  Select,
  sanitizeToNumeric,
  SubAccountsLists,
  formattedCurrency,
} from 'vopay';
import { AccountTransferRequest } from './types';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import useAccountBalance from '@/hooks/useApi/useAccountBalance';
import useSubAccounts from '@/hooks/useApi/useSubAccounts';

type AccountTransferProps = {
  currency: SupportedCurrencies;
  token: string;
};

export function useAccountTransfer() {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { response } = useClientData();
  const { state: accountTransferState } = useLocation<AccountTransferProps>();
  const tokenProp = accountTransferState?.token;
  const currencyProp = accountTransferState?.currency;
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.AccountTransfer' });

  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { subAccounts, fetchSubAccounts, transferAccountFrom, transferAccountTo } = useSubAccounts();
  const { balance, fetchBalance } = useAccountBalance();
  const [isUnderReview, setIsUnderReview] = useState(false);

  const currency = currencyProp || (response.Country === 'US' ? 'USD' : 'CAD');
  const selectedAccount =
    tokenProp !== undefined ? subAccounts.find((account) => account.Token === tokenProp)?.AccountID || '' : '';

  const fieldDataList = useFormConfig(
    response.AccountID,
    response.AccountName,
    balance?.AvailableFunds?.toString() ?? '0.00',
    subAccounts,
  );

  const { renderFormElements, ctx } = useForm<AccountTransferRequest>({
    config: fieldDataList,
    initialValues: {
      Currency: currency,
      From: selectedAccount,
      To: '',
      Amount: 0,
    },
    onSubmit: async (serializedData) => {
      const redirectToTransferDetails = (transactionId: string) => {
        history.push({
          pathname: `/transactions-history/details/AccountTransfer/${transactionId}/${ctx.values.Currency}/`,
          state: {
            transactionId,
            activeModule: 'accountTransfer',
            hasNewTransactionButton: true,
          },
        });
      };

      const callTransferTo = async () =>
        transferAccountTo({
          Amount: serializedData.Amount,
          RecipientAccountID: ctx.values.To,
          Currency: ctx.values.Currency,
        });

      const callTransferFrom = async () =>
        transferAccountFrom({
          Amount: serializedData.Amount,
          DebitorAccountID: ctx.values.From,
          Currency: ctx.values.Currency,
        });

      try {
        setDataFetchingInfo(true);

        const fromMain = response.AccountID === ctx.values.From;
        const toMain = response.AccountID === ctx.values.To;

        if (fromMain) {
          const res = await callTransferTo();
          redirectToTransferDetails(res.TransactionID);
        } else if (toMain) {
          const res = await callTransferFrom();
          redirectToTransferDetails(res.TransactionID);
        } else {
          await callTransferFrom();
          const res = await callTransferTo();
          redirectToTransferDetails(res.TransactionID);
        }
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('transferErrorMessage', { defaultValue: 'Failed sub-account transfer' });
        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      } finally {
        setDataFetchingInfo(false);
      }
    },
    isReviewing: isUnderReview,
  });

  useEffect(() => {
    const getSubAccounts = async () => {
      try {
        setDataFetchingInfo(true);
        await fetchSubAccounts({});
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('subAccountErrorMessage', { defaultValue: 'Failed to fetch sub-accounts' });
        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      } finally {
        setDataFetchingInfo(false);
      }
    };

    getSubAccounts();
  }, []);

  useEffect(() => {
    const getBalance = async () => {
      try {
        await fetchBalance(ctx.values.Currency || 'CAD');
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('balanceErrorMessage', { defaultValue: 'Failed to fetch the account balance' });
        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      }
    };
    getBalance();
  }, [ctx.values.Currency]);

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config: fieldDataList }),
    isUnderReview,
    setIsUnderReview,
  };
}

function useFormConfig(
  mainAccountId: string,
  mainAccountName: string,
  mainAccountBalance: string,
  subAccounts: SubAccountsLists[],
): FieldData<AccountTransferRequest>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.AccountTransfer' });
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedDebitorAccountID, setSelectedDebitorAccountID] = useState('');
  const [selectedRecipientAccountID, setSelectedRecipientAccountID] = useState('');

  const balances = Object.values(
    subAccounts.find((subAccount) => subAccount.AccountID === selectedDebitorAccountID)?.Balances || [],
  );

  const availableDebitorBalance =
    mainAccountId === selectedDebitorAccountID
      ? mainAccountBalance
      : balances.filter((balancesSub) => balancesSub.Currency === selectedCurrency)[0]?.AvailableBalance || '0.00';

  const CurrencyOptions = [
    { label: 'CAD', value: 'CAD' },
    { label: 'USD', value: 'USD' },
  ];

  const fromAccountsOptions = buildSubAccountOptions(
    subAccounts.filter((data) => selectedRecipientAccountID !== data.AccountID && data.AccountStatus === 'Active'),
    selectedCurrency,
    selectedRecipientAccountID === mainAccountId,
  );
  const toAccountsOptions = buildSubAccountOptions(
    subAccounts.filter((data) => selectedDebitorAccountID !== data.AccountID && data.AccountStatus === 'Active'),
    selectedCurrency,
    selectedDebitorAccountID === mainAccountId,
  );

  return [
    {
      label: () => t('currency', { defaultValue: 'Currency' }),
      fieldName: 'Currency',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            ctx.setFieldValue('Currency', e.target.value);
            setSelectedCurrency(e.target.value);
          },
        });
        return <Select version="2" options={CurrencyOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('amount', { defaultValue: 'Amount' }),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: AccountTransferRequest) => Number(sanitizeToNumeric(String(values.Amount))),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="currency" {...fieldData} />;
      },
      validate: () =>
        Yup.string()
          .test(
            'amount-min-validation',
            t('amountValidation', { defaultValue: 'This value should be greater than or equal to $0.01.' }),
            (value) => {
              const amountValidation = Number(value) <= 0.0;
              return !amountValidation;
            },
          )
          .test(
            'amount-max-validation',
            `${t('maxAmountValidation', { defaultValue: 'Insufficient funds. Maximum allowed is:' })} $${String(availableDebitorBalance)}`,
            (value) => {
              if (!selectedDebitorAccountID) return true;
              const availableFundsValidation = Number(sanitizeToNumeric(value)) > Number(availableDebitorBalance);
              return !availableFundsValidation;
            },
          )
          .required(),
    },
    {
      label: () => t('from', { defaultValue: 'From' }),
      fieldName: 'From',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            ctx.setFieldValue('From', e.target.value);
            setSelectedDebitorAccountID(e.target.value);
          },
        });
        return (
          <Select
            version="2"
            sort={false}
            placeholder={t('selectPlaceholder', { defaultValue: 'Please Select...' })}
            options={fromAccountsOptions}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('to', { defaultValue: 'To' }),
      fieldName: 'To',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            ctx.setFieldValue('To', e.target.value);
            setSelectedRecipientAccountID(e.target.value);
          },
        });
        return (
          <Select
            version="2"
            sort={false}
            placeholder={t('selectPlaceholder', { defaultValue: 'Please Select...' })}
            options={toAccountsOptions}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];

  function buildSubAccountOptions(
    lists: SubAccountsLists[],
    currencySelected: string,
    removeMain: boolean,
  ): { label: string; value: string }[] {
    const initial: { label: string; value: string }[] = removeMain
      ? []
      : [{ label: `${mainAccountName} ($${formattedCurrency(mainAccountBalance) || '0.00'})`, value: mainAccountId }];

    const sorted = [...lists].sort((a, b) => a.AccountName.toUpperCase().localeCompare(b.AccountName.toUpperCase()));

    return [
      ...initial,
      ...sorted.map((data) => ({
        label: `${data.AccountName} ($${formattedCurrency(
          Object.values(data.Balances).filter((balances) => balances.Currency === currencySelected)[0]
            ?.AvailableBalance || '0.00',
        )})`,
        value: data.AccountID,
      })),
    ];
  }
}
